<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import Dialog from "./ui/dialog/Dialog.vue";
import DialogOverlay from "./ui/dialog/DialogOverlay.vue";
import DialogPanel from "./ui/dialog/DialogPanel.vue";
import DialogTitle from "./ui/dialog/DialogTitle.vue";
import { DialogDescription } from "@headlessui/vue";
import { useAuthStore } from "../pinia/auth";

const { t } = useI18n();

const authStore = useAuthStore();
const theme = computed(() => authStore.theme?.Accent);
const highContrast = computed(() => authStore.theme?.HighAccessibility);

export interface GrantCredentials {
  ticketNumber?: string;
  totpCode?: string;
}

export interface ACLRequirements {
  requireTicketNumber: boolean;
  requireTOTP: boolean;
  description: string;
}

const props = defineProps<{
  show: boolean;
  acl: ACLRequirements | null;
  error?: string | null;
  isLoading?: boolean;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "submit", credentials: GrantCredentials): void;
}>();

// Form state
const ticketNumber = ref("");
const totpCode = ref("");
const validationErrors = ref<{ ticketNumber?: string; totpCode?: string }>({});

// Reset form when dialog opens/closes
watch(
  () => props.show,
  (newValue) => {
    if (newValue) {
      ticketNumber.value = "";
      totpCode.value = "";
      validationErrors.value = {};
    }
  },
);

const requiresTicketNumber = computed(() => props.acl?.requireTicketNumber ?? false);
const requiresTOTP = computed(() => props.acl?.requireTOTP ?? false);

function validate(): boolean {
  validationErrors.value = {};
  let isValid = true;

  if (requiresTicketNumber.value && !ticketNumber.value.trim()) {
    validationErrors.value.ticketNumber = t("err.fieldRequired");
    isValid = false;
  }

  if (requiresTOTP.value) {
    const code = totpCode.value.trim();
    if (!code) {
      validationErrors.value.totpCode = t("err.fieldRequired");
      isValid = false;
    } else if (!/^\d{6}$/.test(code)) {
      validationErrors.value.totpCode = t("err.totpMustBe6Digits");
      isValid = false;
    }
  }

  return isValid;
}

function handleSubmit() {
  if (!validate()) {
    return;
  }

  const credentials: GrantCredentials = {};

  if (requiresTicketNumber.value) {
    credentials.ticketNumber = ticketNumber.value.trim();
  }

  if (requiresTOTP.value) {
    credentials.totpCode = totpCode.value.trim();
  }

  emit("submit", credentials);
}

function handleClose() {
  emit("close");
}
</script>

<template>
  <Dialog :show="show" @close="handleClose">
    <DialogOverlay />

    <DialogPanel class="sm:max-w-md">
      <DialogTitle>
        {{ t("dialogs.grantCredentials.title") }}
      </DialogTitle>

      <DialogDescription class="text-sm text-muted-foreground">
        {{ t("dialogs.grantCredentials.description", { name: acl?.description ?? "" }) }}
      </DialogDescription>

      <form class="mt-6 space-y-4" @submit.prevent="handleSubmit">
        <!-- Server Error -->
        <div v-if="error" class="rounded-md border border-destructive bg-destructive/10 p-3">
          <p class="text-sm text-destructive">{{ error }}</p>
        </div>

        <!-- Ticket Number Field -->
        <div v-if="requiresTicketNumber">
          <label for="ticket-number" class="block text-sm font-medium text-foreground">
            {{ t("dialogs.grantCredentials.fields.ticketNumber.label") }}
          </label>
          <p class="mb-2 text-xs text-muted-foreground">
            {{ t("dialogs.grantCredentials.fields.ticketNumber.description") }}
          </p>
          <input
            id="ticket-number"
            v-model="ticketNumber"
            type="text"
            autocomplete="off"
            :disabled="isLoading"
            :placeholder="t('dialogs.grantCredentials.fields.ticketNumber.placeholder')"
            :class="[
              'flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background',
              'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
              validationErrors.ticketNumber ? 'border-destructive' : 'border-input',
            ]"
          />
          <p v-if="validationErrors.ticketNumber" class="mt-1 text-xs text-destructive">
            {{ validationErrors.ticketNumber }}
          </p>
        </div>

        <!-- TOTP Code Field -->
        <div v-if="requiresTOTP">
          <label for="totp-code" class="block text-sm font-medium text-foreground">
            {{ t("dialogs.grantCredentials.fields.totpCode.label") }}
          </label>
          <p class="mb-2 text-xs text-muted-foreground">
            {{ t("dialogs.grantCredentials.fields.totpCode.description") }}
          </p>
          <input
            id="totp-code"
            v-model="totpCode"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="6"
            autocomplete="one-time-code"
            :disabled="isLoading"
            :placeholder="t('dialogs.grantCredentials.fields.totpCode.placeholder')"
            :class="[
              'flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background',
              'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
              validationErrors.totpCode ? 'border-destructive' : 'border-input',
            ]"
          />
          <p v-if="validationErrors.totpCode" class="mt-1 text-xs text-destructive">
            {{ validationErrors.totpCode }}
          </p>
        </div>

        <!-- Actions -->
        <div class="flex justify-end space-x-2 pt-4">
          <button
            type="button"
            :disabled="isLoading"
            class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
            @click="handleClose"
          >
            {{ t("common.cancel") }}
          </button>
          <button
            type="submit"
            :disabled="isLoading"
            class="themed-button h-10 px-4"
            :class="(Boolean(theme) && `theme-${theme}${highContrast ? '-hc' : ''}`) || 'theme-default'"
          >
            <span
              v-if="isLoading"
              class="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
            ></span>
            {{ t("dialogs.grantCredentials.submit") }}
          </button>
        </div>
      </form>
    </DialogPanel>
  </Dialog>
</template>
